<template>
	<v-row>
		<v-col>
			<div v-for="(campaign, campaignIdx) in campaigns" :key="campaign.id">

				<form-divider v-if="campaignIdx > 0"/>

				<v-form
						v-model="formIsValid.campaigns[campaignIdx]"
						ref="thingsTemplateForm"
						class="mb-8"
						lazy-validation
				>

					<ui-box
							ref="addConditions"
							eager
							:status="conditionsValidStatus(campaignIdx)"
							newForms
					>
						<!-- Empty Campaign - Remove Message-->
						<template
								v-if="(bypassDefaultRequired || campaignIdx > 0) 
									&& campaigns.length > 1 
									&& getRemainingPlaces[campaignIdx].length === 0"
						>

							<v-alert
									border="right"
									class="mb-4"
									:class="{
												'me-12': $vuetify.breakpoint.mdAndUp,
												'me-8': $vuetify.breakpoint.smAndDown
											}"
									outlined
									type="error"
							>
								<div class="align-center d-flex">
									<div class="flex-grow-1 pe-4 pe-sm-8">
										<template v-if="campaignIdx === 0">
											Your main conditions have no places remaining to be defined. If you
											remove it your remaining conditions will take its place. You may
											safely remove it.
										</template>
										<template v-else>
											There are currently no places remaining to define these conditions.
											You may safely remove it.
										</template>
									</div>
									<div>
										<v-btn
												class="rounded-lg"
												color="error"
												depressed
												small
												@click="removeCampaign(campaignIdx)"
										>
											Remove
										</v-btn>
									</div>
								</div>
							</v-alert>

						</template>
						<!-- END: Empty Campaign - Remove Message-->

						<!-- Main Campaign Header-->
						<template v-if="campaignIdx === 0">
							<form-title :title="`${template.name} Conditions`">
								These are the conditional things that places could need to join your
								pass. We'll show what's available to you based on the type of pass you
								choose. The current pass type
								<span class="font-weight-bold">[{{ template.name }}]</span>
								allows you to add the following conditional things to your pass.
							</form-title>
						</template>
						<!-- END: Main Campaign Header-->

						<!-- Additional Campaign Header-->
						<template v-else>
							<form-title :title="`${campaignIdx}. Alternative Conditions`">
								You may have some places that are not participating directly in the main
								conditions of your pass. You can use this section to create specific
								conditions for these places.

								<template v-slot:button>
									<v-btn
											class="mt-1 rounded-lg"
											color="error"
											depressed
											outlined
											small
											@click="removeCampaign(campaignIdx)"
									>
										<v-icon>mdi-close</v-icon>
										Remove
									</v-btn>
								</template>
							</form-title>
						</template>
						<!-- END: Additional Campaign Header-->

						<form-divider inner/>

						<!-- Empty Campaign Opacity Wrapper-->
						<div
								:class="{ 
									'opacity-30': campaignIdx > 0 
										&& getRemainingPlaces[campaignIdx].length === 0
								}"
						>

							<v-row
									v-if="campaignProductTemplates?.length || false"
							>
								<v-col>

									<bw-table
											v-for="(productTemplate, templateIdx) in availableProductTemplates"
											v-if="productTemplate.type !== 'check-in'"
											:ref="`productTemplate-${productTemplate.id}-table-${templateIdx}`"
											:key="`productTemplate-${productTemplate.id}-table-${templateIdx}`"
											aria-label="data-table"
											:class="{ 'mt-0': templateIdx > 0 }"
											dense
											:expanded="getProductReq(productTemplate.id, campaignIdx).length > 0 ? [productTemplate] : []"
											:headers="templateHeaders"
											hide-default-header
											hide-default-footer
											hide-row-select
											hide-search
											:items="[productTemplate]"
											show-expand
											single-expand
									>
										<template v-slot:item="{ expand, isExpanded, item }">

											<!-- Don't show check-ins -->
											<tr
													v-if="item.type !== 'check-in'"
													:class="{ 'mark-done': item.type === 'check-in' }"
											>
												<td style="padding-right: 0; width: 1%;">
													<template v-if="item.type !== 'check-in'">
														<v-badge
																bordered
																:content="getProductReq(item.id, campaignIdx).length > 0 ? 1 : 0"
																overlap
																:value="getProductReq(item.id, campaignIdx).length > 0 && !isExpanded"
														>
															<v-btn
																	depressed
																	fab
																	outlined
																	small
																	@click.stop="expand(!isExpanded)"
															>
																<template v-if="isExpanded">
																	<v-icon>
																		mdi-chevron-up
																	</v-icon>
																</template>
																<template v-else>
																	<v-icon>
																		mdi-chevron-down
																	</v-icon>
																</template>
															</v-btn>
														</v-badge>
													</template>
												</td>
												<td
														:style="{ 
																	paddingRight: getProductReq(item.id, campaignIdx).length === 0 ? '1rem !important' : '0 !important',
																}"
												>
													<div class="d-flex">
														<div
																class="flex-grow-1"
																:class="{
																		'font-weight-bold text-subtitle-2': $vuetify.breakpoint.smAndUp,
																		'font-weight-medium text-caption': $vuetify.breakpoint.xsOnly
																	}"
														>
															{{ item.name }}
														</div>
														<div>
															<div class="d-flex">
																<v-chip
																		class="font-weight-bold ms-2 rounded-r-0"
																		:class="{
																				'opacity-50': productReqStatus(item, campaignIdx) !== 'optional'
																			}"
																		:color="productReqStatus(item, campaignIdx) === 'optional' ? 'grey darken-1' : 'grey'"
																		outlined
																		pill
																		:text-color="productReqStatus(item, campaignIdx) === 'optional' ? 'grey darken-1' : 'grey'"
																		:small="$vuetify.breakpoint.smAndUp"
																		:x-small="$vuetify.breakpoint.xsOnly"
																>
																	Optional
																</v-chip>

																<v-chip
																		class="font-weight-bold rounded-l-0"
																		:class="{
																				'opacity-50': productReqStatus(item, campaignIdx) !== 'required'
																			}"
																		:color="productReqStatus(item, campaignIdx) === 'required' ? 'error' : 'grey'"
																		outlined
																		pill
																		:small="$vuetify.breakpoint.smAndUp"
																		:x-small="$vuetify.breakpoint.xsOnly"
																>
																	Required
																</v-chip>
															</div>

														</div>
													</div>
												</td>
												<td
														:style="{ 
																	paddingLeft: 0,
																	width: getProductReq(item.id, campaignIdx).length === 0 ? '1%' : '0% !important'
																}"
												>
													<div
															v-if="getProductReq(item.id, campaignIdx).length === 0"
															class="d-flex justify-end">
														<v-btn
																color="success"
																depressed
																fab
																small
																@click="addNewProductReq(item, campaignIdx)"
														>
															<v-icon>mdi-plus</v-icon>
														</v-btn>
													</div>
												</td>
											</tr>
										</template>

										<template v-slot:expanded-item="{ headers, item }">
											<tr class="expanded">
												<td style="width: 44px;"></td>
												<td :colspan="headers.length - 1">
													<template
															v-if="getProductReq(item.id, campaignIdx).length === 0"
													>
															<span class="font-italic grey--text text-body-2">
																Click the green plus button to add a condition.
															</span>
													</template>
													<template
															v-else
															v-for="(req, idx) in getProductReq(item.id, campaignIdx)"
													>
														<div
																:key="`req-${campaignIdx}-${req.product_template}-${idx}`"
														>

															<v-row class="align-center">
																<v-col cols="12" sm="">

																	<div
																			class="font-weight-medium position-relative"
																			:class="{
																					'text-body-2': $vuetify.breakpoint.smAndUp,
																					'text-caption': $vuetify.breakpoint.xsOnly
																				}"
																	>
																		{{ req.product_title }}
																	</div>

																	<div
																			class="grey--text mt-1 text--darken-2"
																			:class="{
																				'text-body-2': $vuetify.breakpoint.smAndUp,
																				'text-caption': $vuetify.breakpoint.xsOnly
																			}"
																	>
																		{{ req.product_instructions }}
																	</div>

																	<div v-if="req?.child_templates?.length"
																			 class="mt-2">
																		<v-chip
																				v-for="childId in req.child_templates"
																				:key="`req-${campaignIdx}-${childId}`"
																				class="me-1 mt-1"
																				color="accent lighten-5"
																				label
																				light
																				x-small
																		>
																				<span class="accent--text text--darken-2">
																					{{
																						getChildTemplateById(req, childId)?.name
																					}}
																				</span>
																		</v-chip>
																	</div>

																	<v-divider
																			v-if="$vuetify.breakpoint.xsOnly"
																			class="mb-n2 mt-2 opacity-30"
																	></v-divider>

																</v-col>
																<v-col sm="auto">


																</v-col>
																<v-col cols="auto">
																	<div class="d-flex justify-end">
																		<v-btn
																				color="secondary"
																				depressed
																				fab
																				outlined
																				small
																				@click="editProductReq(req, campaignIdx)"
																		>
																			<v-icon>
																				mdi-pencil-outline
																			</v-icon>
																		</v-btn>
																		<v-divider
																				class="mx-2 my-1"
																				vertical
																		></v-divider>
																		<v-btn
																				color="error"
																				depressed
																				:disabled="!canRemoveRequirements"
																				fab
																				outlined
																				small
																				@click="confirmRemoveProductReq(req, campaignIdx)"
																		>
																			<v-icon>
																				mdi-trash-can-outline
																			</v-icon>
																		</v-btn>
																	</div>
																</v-col>
															</v-row>
														</div>
													</template>
												</td>
											</tr>
										</template>
									</bw-table>

								</v-col>
							</v-row>

							<v-row v-if="maxCampaigns > 1">
								<v-col>

									<!-- Check-Ins -->
									<template v-if="template?.settings?.places_to_check_in?.hide === false">
										<div class="align-center field-actions d-flex justify-start">
											<v-checkbox
													v-model="campaigns[campaignIdx].onboard_settings.autoAddCheckIn"
													class="mb-n2 mt-2"
													:false-value="false"
													:true-value="true"
											>
												<template v-slot:label>
													<span :class="{
														'text-body-2': $vuetify.breakpoint.mdAndUp,
														'text-caption': $vuetify.breakpoint.smAndDown
													}">
														Auto-include check-ins for these places in the pass
													</span>
												</template>
											</v-checkbox>
										</div>
									</template>
									<!-- END: Check-Ins -->

									<bw-search-select
											v-model="campaigns[campaignIdx].venueIds"
											:ref="`participatingPlaces${campaignIdx}`"
											hide-details="auto"
											:items="getRemainingPlaces[campaignIdx]"
											item-text="name"
											item-value="id"
											label="Participating Places"
											multiple
											:rules="campaignIdx === 0 ? rules.pass_requirements : rules.additional_requirements"
									/>
								</v-col>
							</v-row>

						</div>
						<!-- END: Empty Campaign Opacity Wrapper-->

					</ui-box>

				</v-form>

			</div>

			<v-alert
					v-if="canAddCampaign"
					class="mb-8"
					outlined
					rounded="lg"
					type="success"
			>
				<div class="align-center d-flex">
					<div class="flex-grow-1 pe-4 pe-sm-8">
						Looks like you might need additional/special conditions for some places
					</div>
					<div>
						<v-btn
								color="success"
								depressed
								fab
								small
								@click="addCampaign"
						>
							<v-icon>
								mdi-plus
							</v-icon>
						</v-btn>
					</div>
				</div>
			</v-alert>

			<!-- Save Form -->
			<save-form
					:refs="$refs"
					:additional-validations="[campaignsAreValid]"
					:is-saving="isSaving"
					new-forms
					save-text="Save"
					@cancel-action="exit"
					@save-action="save"
			></save-form>
			<!-- END: Save Form -->

			<!-- New Requirement Modal -->
			<v-dialog
					v-if="newOrEditProductReq"
					content-class="rounded-xl"
					persistent
					scrollable
					:value="true"
					width="800px"
			>
				<v-form
						v-model="formIsValid.productReq"
						ref="formProductRequirements"
				>
					<v-card class="position-relative" rounded="xl">
						<v-card-title class="d-flex justify-space-between ma-0 ma-md-4">
							<div class="w-100">
								<div class="align-center d-flex justify-space-between">
									<h6>{{ newOrEditProductReq.product_template_name }}</h6>
								</div>
							</div>
						</v-card-title>
						<v-card-text class="text-body-2">

							<v-alert
									border="right"
									class="mb-4 mb-sm-8"
									:class="{ 'text-caption': $vuetify.breakpoint.xsOnly }"
									dense
									outlined
									rounded="lg"
									type="info"
							>
								Remember, you're asking your places to provide you with this item so
								give a meaningful title and instructions.
							</v-alert>

							<v-row>
								<v-col>
									<v-text-field
											v-model="newOrEditProductReq.product_title"
											class="required rounded-lg"
											hide-details="auto"
											label="Title"
											outlined
											:rules="rules.newOrEditProductReq.product_title"
											type="text"
									></v-text-field>
								</v-col>
							</v-row>

							<v-row>
								<v-col>
									<v-text-field
											v-model="newOrEditProductReq.product_instructions"
											class="required rounded-lg"
											hide-details="auto"
											label="Instructions"
											outlined
											:rules="rules.newOrEditProductReq.product_instructions"
											type="text"
									></v-text-field>
								</v-col>
							</v-row>

							<v-row v-if="hasChildTemplates(newOrEditProductReq)">
								<v-col>
									<v-select
											v-model="newOrEditProductReq.child_templates"
											chips
											class="rounded-lg"
											clearable
											deletable-chips
											hide-details="auto"
											:items="getChildTemplates(newOrEditProductReq)"
											item-text="name"
											item-value="id"
											label="Child Templates"
											multiple
											outlined
											small-chips
											@change=""
									></v-select>
								</v-col>
							</v-row>

							<v-row >
								<v-col>
									<v-switch
											v-model="newOrEditProductReq.min"
											class="mt-3"
											dense
											:false-value="0"
											hide-details="auto"
											inset
											label="Make this a requirement"
											:true-value="1"
									></v-switch>
								</v-col>
							</v-row>

						</v-card-text>

						<v-card-actions>
							<div class="align-center d-flex justify-space-between w-100">
								<span></span>
								<div class="d-flex justify-end">
									<v-btn
											class="me-2 me-md-4 rounded-lg"
											color="grey darken-1"
											depressed
											:large="$vuetify.breakpoint.mdAndUp"
											text
											@click="cancelNewOrUpdateProductReq"
									>
										Cancel
									</v-btn>
									<v-btn
											v-if="newOrEdit === 'new'"
											class="rounded-lg"
											color="primary"
											depressed
											:disabled="!formIsValid.productReq"
											:large="$vuetify.breakpoint.mdAndUp"
											@click="saveNewProductReq"
									>
										Save
									</v-btn>
									<v-btn
											v-if="newOrEdit === 'edit'"
											class="rounded-lg"
											color="primary"
											depressed
											:disabled="!formIsValid.productReq"
											:large="$vuetify.breakpoint.mdAndUp"
											@click="updateProductReq"
									>
										Update
									</v-btn>
								</div>
							</div>
						</v-card-actions>
					</v-card>
				</v-form>
			</v-dialog>
			<!-- END: New Requirement Modal -->

			<!-- Empty Campaigns -->
			<v-dialog
					content-class="rounded-xl"
					persistent
					scrollable
					width="600px"
					:value="showEmptyCampaignsModal"
			>
				<v-card class="position-relative" rounded="xl">
					<v-card-title class="d-flex justify-space-between ma-0 ma-md-4">
						<div class="w-100">
							<div class="align-center d-flex justify-space-between">
								<h6>Empty Conditions</h6>
							</div>
						</div>
					</v-card-title>
					<v-card-text class="text-body-2">

						<v-row>
							<v-col>
								You have alternative conditions that have no requirements defined. Would
								you like to edit them or safely remove them and continue saving?
							</v-col>
						</v-row>

					</v-card-text>

					<v-card-actions>
						<div class="align-center d-flex justify-space-between w-100">
							<span></span>
							<div class="d-flex justify-end">
								<v-btn
										class="me-2 me-md-4 rounded-lg"
										color="grey darken-1"
										depressed
										:large="$vuetify.breakpoint.mdAndUp"
										outlined
										@click="showEmptyCampaignsModal = false"
								>
									Edit
								</v-btn>
								<v-btn
										class="rounded-lg"
										color="primary"
										depressed
										:large="$vuetify.breakpoint.mdAndUp"
										@click="removeEmptyCampaignsAndSave"
								>
									Remove & Save
								</v-btn>
							</div>
						</div>
					</v-card-actions>
				</v-card>
			</v-dialog>
			<!-- END: Empty Campaigns -->

			<!-- Remove Requirement Modal -->
			<v-dialog
					content-class="rounded-xl"
					persistent
					scrollable
					width="600px"
					:value="!!removeProductReqTemplateId"
			>
				<v-card class="position-relative" rounded="xl">
					<v-card-title class="d-flex justify-space-between ma-0 ma-md-4">
						<div class="w-100">
							<div class="align-center d-flex justify-space-between">
								<h6>Remove Condition</h6>
							</div>
						</div>
					</v-card-title>
					<v-card-text class="text-body-2">

						<v-row>
							<v-col>
								Are you sure you would like to remove this condition?
							</v-col>
						</v-row>

					</v-card-text>

					<v-card-actions>
						<div class="align-center d-flex justify-space-between w-100">
							<span></span>
							<div class="d-flex justify-end">
								<v-btn
										class="me-2 me-md-4 rounded-lg"
										color="grey darken-1"
										depressed
										:large="$vuetify.breakpoint.mdAndUp"
										text
										@click="cancelRemoveProductReq"
								>
									Cancel
								</v-btn>
								<v-btn
										class="rounded-lg"
										color="primary"
										depressed
										:large="$vuetify.breakpoint.mdAndUp"
										@click="removeProductReq"
								>
									Confirm
								</v-btn>
							</div>
						</div>
					</v-card-actions>
				</v-card>
			</v-dialog>
			<!-- END: Remove Requirement Modal -->

		</v-col>
	</v-row>
</template>

<script>
import {Campaign, OnboardSettings, ProductReq} from '../../../JSON/Entities';
import {scrollToRef} from '../../../mixins';
import {mapActions} from "vuex";

export default {
	name: "Things",
	mixins: [scrollToRef],
	props: {
		campaignProductTemplates: {
			type: Array,
			default: () => []
		},
		checkInTemplates: {
			type: Array,
			default: () => []
		},
		partner: {
			type: Object,
			default: () => ({})
		},
		pass: {
			type: Object,
			default: () => ({})
		},
		places: {
			type: Array,
			default: () => []
		},
		productTemplates: {
			type: Array,
			default: () => []
		},
		template: {
			type: Object,
			default: () => ({})
		}
	},
	data() {
		return {
			bypassDefaultRequired: true,
			campaigns: [],
			checkInTemplateId: null,
			formIsValid: {
				campaigns: [],
				checkIns: false
			},
			isSaving: false,
			maxCampaigns: 9999,
			newOrEdit: null,
			newOrEditProductReq: null,
			newOrEditCampaignIdx: null,
			removeProductReqTemplateId: null,
			removeProductReqCampaignIdx: null,
			rules: {
				newOrEditProductReq: {
					product_instructions: [
						v => !!v || 'Instructions are required'
					],
					product_title: [
						v => !!v || 'Title is required'
					]
				},
				checkIns: {
					check_in_validation: [
						v => !!v || 'Check-in validation type is required'
					]
				},
				template: [
					v => !!v || 'Template is required'
				],
				pass_requirements: [
					v => {
						const venueCount = this.campaigns[0]?.venueIds?.length || 0;
						const hasProductReqs = this.hasProductReqs(0);

						if(!venueCount && hasProductReqs) {
							return "You must add places for your condition or remove if unused";
						}

						if(venueCount && !hasProductReqs) {
							return true;
						}

						return !!((venueCount && hasProductReqs) || (!venueCount && !hasProductReqs));
					}
				],

				additional_requirements: [
					v => {
						const venueCount = this.campaigns[1]?.venueIds?.length || 0;
						const productReqCount = this.campaigns[1]?.onboard_settings?.productReqs?.length || 0;

						return !!((venueCount && productReqCount) || (!venueCount && !productReqCount));
					}
				]
			},
			selectedCampaignProductTemplates: [],
			showEmptyCampaignsModal: false,
			templateHeaders: [
				{
					align: 'start',
					mandatory: true,
					text: 'Name',
					value: 'name'
				},
				{
					align: 'start',
					mandatory: true,
					text: 'Required/Optional',
					value: 'min'
				},
				{
					align: 'start',
					mandatory: true,
					text: 'Actions',
					sortable: false,
					value: 'actions',
				}
			]
		};
	},

	created() {
		// Deep clone the incoming pass campaigns
		const defaultCampaign = new Campaign({}, this.template);
		defaultCampaign.onboard_settings = {
			productReqs: cloneDeep(this.campaignProductTemplates.filter(t => t.type !== 'check-in'))
		};
		this.campaigns = this.pass.campaigns.length
				? this.pass.campaigns.map(campaign => cloneDeep(campaign))
				: [defaultCampaign];
		// Ensure product requirements are ready
		this.campaigns.forEach((campaign, idx) => {

			// Set the campaign form sections validity (false to start by default)
			this.$set(this.formIsValid.campaigns, idx, false);

			// Use JSON Entities template for Onboard Settings
			const onboardSettings = new OnboardSettings(campaign.onboard_settings || {}, this.template);

			// Map the Product Reqs to the Onboard Settings
			onboardSettings.productReqs.map(req => {

				// Find the associated campaign product template for missing info
				const productTemplate = this.campaignProductTemplates
						.find(template => template.id === req.product_template);

				// Set name and type from productTemplate if not existing in req
				req = {
					...req,
					product_template_name: req.product_template_name ?? (productTemplate?.name ?? null),
					product_template_type: req.product_template_type ?? (productTemplate?.type ?? null),
				};

				// Use JSON Entities template for Product Reqs
				if(productTemplate) {
					new ProductReq(req);
				}
				else {
					// Handle error if product template is not found
					console.error('Product template not found for req:', req);
				}
			});

			// Use $set to ensure reactivity
			this.$set(campaign, 'onboard_settings', onboardSettings);
			this.checkInTemplateId = this.campaigns[0].onboard_settings.checkin_template_id;
		});
	},

	computed: {
		// Filter the check-in from the templates for selection
		availableProductTemplates() {
			return this.campaignProductTemplates.filter(template => template.type !== 'check-in');
		},

		campaignsAreValid() {
			return this.formIsValid.campaigns.every(campaign => !!campaign);
		},

		canAddCampaign() {
			// No campaign yet... return false
			if(!this.campaigns.length) return false;

			// Check if maximum number of selects has been reached
			if(this.campaigns.length >= this.maxCampaigns) {
				return false;
			}

			// Check if current campaign has no defined reqs
			const currentCampaign = this.campaigns[this.campaigns.length - 1];
			// const hasProductReqs = currentCampaign.onboard_settings.productReqs
			// 		.filter(req => req.product_template_type !== 'check-in').length;
			// if(!hasProductReqs) {
			// 	return false;
			// }

			// Check if any prior selects are empty
			if(this.campaigns.some(campaign => (campaign.venueIds || []).length === 0)) {
				return false;
			}

			// Calculate all selected so far
			const alreadySelectedPlaces = this.campaigns
					.flatMap(campaign => campaign.venueIds || [])
					.flat();

			// Check if there are any remaining places
			return this.places.some(
					(place) => !alreadySelectedPlaces.includes(place.id)
			);
		},

		getRemainingPlaces() {
			return this.campaigns.map((_, campaignIdx) => {
				// Use flatMap to get all selected venueIds
				const alreadySelectedPlaces = this.campaigns
						.flatMap(campaign => campaign.venueIds || [])
						.filter(venueId => !this.campaigns[campaignIdx].venueIds.includes(venueId));

				return this.places.filter(
						(place) => !alreadySelectedPlaces.includes(place.id)
				);
			});
		},

		mainCampaignFormIsValid() {
			return this.formIsValid.campaigns[0];
		}
	},
	mounted() {
		const productsReqs = this.campaigns[0].onboard_settings.productReqs ?? [];
		if(!productsReqs.filter(productsReq => !!productsReq.product_template).length) {
			this.campaignProductTemplates.filter(t => t.type !== 'check-in').forEach(template => {
				this.addNewProductReq(template, 0);
				this.saveNewProductReq();
			});
		}
	},
	methods: {
		...mapActions('formState', ['setDirtyState']),

		addCampaign() {
			this.campaigns.push(new Campaign({}, this.template));
		},

		addNewProductReq(item, campaignIdx) {
			this.newOrEdit = 'new';

			const min = item.min || 0;

			this.newOrEditProductReq = new ProductReq({
				min: min,
				product_template: item.id,
				product_template_name: item.name,
				product_template_type: item.type,
				product_title: item.name,
				product_instructions: item.product_instructions,
			});
			this.newOrEditCampaignIdx = campaignIdx;

			this.$nextTick(() => {
				if(this.campaigns[campaignIdx].venueIds.length && (this.bypassDefaultRequired || campaignIdx > 0)) {
					this.formIsValid.campaigns[campaignIdx] = true;
				}
			});
		},

		conditionsValidStatus(campaignIdx) {
			// No status if we have no product requirements for this campaign 
			// We could decide to show this as a warning in the future or if reqs are removed
			if(!this.hasProductReqs(campaignIdx)) return null;

			// Otherwise let's just return the valid state of the form
			return this.formIsValid.campaigns[campaignIdx] ? 'success' : 'error'
		},

		editProductReq(requirement, campaignIdx) {
			const productReq = this.campaigns[campaignIdx]?.onboard_settings?.productReqs
					.find(productReq => productReq.product_template === requirement.product_template);

			if(!productReq) {
				console.error('Product requirements not found.');
				return null;
			}

			// Clone the product req so if they cancel the original is not altered
			this.newOrEdit = 'edit';
			this.newOrEditProductReq = cloneDeep(productReq);
			this.newOrEditCampaignIdx = campaignIdx;
		},

		editRequirement(requirement, campaignIdx) {
			const productReq = this.campaigns[campaignIdx]?.onboard_settings?.productReqs
					.find(productReq => productReq.product_template === requirement.product_template);

			if(!productReq) {
				console.error('Product requirements not found.');
				return null;
			}

			this.editProductReq = productReq;
			this.editProductReqCampaignIdx = campaignIdx;
		},

		cancelNewOrUpdateProductReq() {
			this.newOrEdit = null;
			this.newOrEditProductReq = null;
			this.newOrEditCampaignIdx = null;
		},

		cancelRemoveProductReq() {
			this.removeProductReqTemplateId = null;
			this.removeProductReqCampaignIdx = null;
		},

		// TODO: This will likely need to be updated - not sure if these dates are true
		canRemoveRequirements() {
			const { kickoff_date = null } = this.localCampaigns[0];
			if(!kickoff_date || kickoff_date === "0000-00-00 00:00:00") {
				return true;
			}
			return moment().isAfter(moment(kickoff_date));
		},

		confirmRemoveProductReq(requirement, campaignIdx) {
			this.removeProductReqTemplateId = requirement.product_template;
			this.removeProductReqCampaignIdx = campaignIdx;
		},

		createFormRef(id) {
			return !isNaN(id) ? `thingsTemplate${id}Form` : null;
		},

		exit() {
			window.location.href = `/partner-portal/${this.partner.id}/passes`;
		},

		getCampaignProductReq(template, campaignIdx) {
			const productReq = this.campaigns[campaignIdx].onboard_settings.productReqs
					.find(productReq => productReq.id === template.id);
			return productReq;
		},

		getChildTemplates(requirement) {
			const templates = this.campaignProductTemplates || [];
			const template = templates.find(t => t.id === requirement.product_template);
			return template?.children || [];
		},

		getChildTemplateById(requirement, childTemplateId) {
			const template = this.campaignProductTemplates
					.find(template => template.id === requirement.product_template);
			return template.children.find(template => template.id === childTemplateId);
		},

		getForm(campaignIdx) {
			return this.$refs?.thingsTemplateForm?.[campaignIdx] || false;
		},

		getProductReq(productTemplateId, campaignIdx) {
			const campaign = this.campaigns[campaignIdx];
			return campaign.onboard_settings.productReqs
					.filter(r => r.product_template === productTemplateId);
		},

		hasChildTemplates(requirement) {
			const children = this.getChildTemplates(requirement);
			return !!children.length;
		},

		hasEmptyCampaigns() {
			// Only if we have more than 1 campaign - we don't want to remove the first one
			if(this.campaigns.length <= 1) return [];

			// Returns an array of campaign indexes that are empty and should be removed
			const emptyCampaignIdxs = this.campaigns.reduce((acc, campaign, idx) => {
				if(idx > 0 && campaign.onboard_settings.productReqs.length === 0) {
					acc.push(idx); // Add the index to the accumulator array if the condition is met
				}
				return acc;
			}, []);

			return emptyCampaignIdxs.sort((a, b) => b - a);
		},

		// Generally we want to know if the campaign has non-check-ins defined
		hasProductReqs(campaignIdx, includeCheckIns = false) {
			return !!this.campaigns[campaignIdx].onboard_settings.productReqs
					.filter(req => includeCheckIns
							? true
							: req.product_template_type !== 'check-in')
					.length;
		},

		hasRequiredOverride(template, campaignIdx) {
			// We currently only care about campaign 0
			if(!this.bypassDefaultRequired && campaignIdx > 0) return false;

			// Find the corresponding campaign product req
			const productReq = this.getCampaignProductReq(template, campaignIdx);

			// If there's no productReq it is likely because this condition is not set yet
			if(!productReq) return false;

			// Check if the productReq was originally optional
			const wasOptional = this.isOriginallyOptional(productReq.product_template);

			// If it was optional and not min > 0 it has an override
			return wasOptional && !!productReq.min;
		},

		hasRequiredProductTemplates() {
			return this.campaignProductTemplates
					.filter(template => template.type !== 'check-in')
					.some(template => template.min > 0);
		},

		// Look at the campaign product templates to check if this is originally set to
		// min = 0/1. If it is 0 we may show UI to make the req manually required.
		isOriginallyOptional(productTemplateId) {
			// If we are bypassing default required this is always true
			if(this.bypassDefaultRequired) return true;

			// Or let's look at the templates to check if required
			const template = this.campaignProductTemplates
					.find(template => template.id === productTemplateId);

			if(!template) {
				console.error(`Template with id: ${productTemplateId} was not found in pass templates.`);
				return false;
			}

			return template.min === 0;
		},

		productReqStatus(template, campaignIdx) {
			const productReq = this.getCampaignProductReq(template, campaignIdx);

			// If there is a product requirement set use it's min 
			if(productReq) return productReq.min === 0 ? 'optional' : 'required';

			// If we are bypassing required min setting or no product requirement and this is 
			// not campaign 1 - it defaults to optional
			if(this.bypassDefaultRequired || campaignIdx > 0) return 'optional';

			// This is the first campaign, use the template settings
			return template.min === 0 ? 'optional' : 'required';
		},

		removeEmptyCampaignsAndSave() {
			const emptyCampaignIdxs = this.hasEmptyCampaigns();
			emptyCampaignIdxs.forEach(campaignIdx => this.removeCampaign(campaignIdx));

			this.$nextTick(() => this.save());
		},

		removeCampaign(idx) {
			this.formIsValid.campaigns.splice(idx, 1);
			this.campaigns.splice(idx, 1);
		},

		hasRequiredProductReqs() {
			// If there are no required product templates in this pass type return true
			const hasRequiredProductTemplates = this.hasRequiredProductTemplates();
			if(!hasRequiredProductTemplates) return true;

			// Gather an array of required template ids
			const requiredTemplateIds = this.campaignProductTemplates
					.filter(template => template.min === 1 && template.type !== 'check-in')
					.map(template => template.id);

			// Get the productReq ids as an array for comparison
			const productReqIds = this.campaigns[0].onboard_settings.productReqs
					.map(productReq => productReq.product_template);

			// If we have them all return true, else false
			return requiredTemplateIds.every(id => productReqIds.includes(id));
		},

		removeProductReq() {
			const campaignIdx = this.removeProductReqCampaignIdx;
			const productTemplateId = this.removeProductReqTemplateId;
			const campaign = this.campaigns[campaignIdx];

			if(campaign) {
				// Filter out the removed product req
				campaign.onboard_settings.productReqs = campaign.onboard_settings.productReqs
						.filter(requirement => requirement.product_template !== productTemplateId);
				const defaultCampaignReq = this.campaignProductTemplates.find(
						template => template.id === productTemplateId
				);
				if(defaultCampaignReq) {
					campaign.onboard_settings.productReqs.push(new ProductReq(defaultCampaignReq));
				}
				

				this.$nextTick(() => {
					// If this is the main campaign and the required product req was removed we need
					// to show error state
					if(!this.bypassDefaultRequired && campaignIdx === 0 && this.formIsValid.campaigns[0] && !this.hasRequiredProductReqs()) {
						this.formIsValid.campaigns[0] = false;
					}

					// Check if we need to remove venues from the campaign
					// if(!this.hasProductReqs(campaignIdx))
					// this.$set(this.campaigns[campaignIdx], 'venueIds', []);

					// Reset the variables
					this.removeProductReqCampaignIdx = null;
					this.removeProductReqTemplateId = null;
				});
			}
		},

		saveNewProductReq() {
			// Get the stored campaignIdx
			const campaignIdx = this.newOrEditCampaignIdx;

			// Push to the correct campaign's product reqs
			const existingReq = this.campaigns[campaignIdx].onboard_settings.productReqs
					.find(req => req.id === this.newOrEditProductReq.product_template);
			if(existingReq) {
				Object.assign(existingReq, this.newOrEditProductReq);
			} else {
				this.campaigns[campaignIdx].onboard_settings.productReqs.push(this.newOrEditProductReq);
			}

			// set the form state to dirty in the form store
			this.setDirtyState(true);

			// Wait for render to validate the newly shown select
			this.$nextTick(() => {

				// If this is the main campaign and the required product req was added we need
				// to possibly update the error state
				if(!this.bypassDefaultRequired && campaignIdx === 0 && this.hasRequiredProductReqs()) {
					this.formIsValid.campaigns[0] = !!this.campaigns[0].venueIds.length;
				}

				this.newOrEditProductReq = null;
				this.newOrEditCampaignIdx = null;

				this.$refs[`participatingPlaces${campaignIdx}`][0].$refs.bwSearchSelect.validate();
			});
		},

		save() {
			this.isSaving = true;
			const url = `/partner-portal/${this.partner.id}/passes/save-campaigns/${this.pass.id || ''}`;
			const isGamified = !!this.pass?.use_points;

			// If there are empty campaigns alert the user
			// const emptyCampaignIdxs = this.hasEmptyCampaigns();
			//
			// if(!!emptyCampaignIdxs.length) {
			// 	this.showEmptyCampaignsModal = true;
			// 	this.isSaving = false;
			// 	return false;
			// }

			axios.put(url, {
				campaigns: this.campaigns,
				checkInTemplateId: this.checkInTemplateId
			})
					.then(({ data }) => {

						const hasInvitedVenues = this.campaigns.reduce((
								acc,
								cur
						) => acc + (cur?.venueIds?.length || 0), 0);
						const hasPinCheckIn = this.campaigns[0]?.check_in_validation === 'pin';
						const hasInvites = hasInvitedVenues || hasPinCheckIn;

						if(hasInvites)
							window.location = `/partner-portal/${this.partner.id}/passes/${this.pass.id}/sequences`;

						else // figure this out later
							window.location = `/partner-portal/${this.partner.id}/passes/${this.pass.id}/things`;
					})
					.catch(error => {
						this.isSaving = false;
						window.EventBus.$emit('system-messages', [
							{ message: error.message, messageType: 'error' }
						]);
					});
		},

		updateLimitChildTemplates() {
			this.campaigns.forEach(campaign => {
				campaign.onboard_settings.productReqs.forEach(req => {
					// Automatically set limit_child_templates based on child_templates length
					req.limit_child_templates = !!req.child_templates?.length > 0;
				});
			});
		},

		updateProductReq() {
			const campaignIdx = this.newOrEditCampaignIdx;
			const { product_template } = this.newOrEditProductReq;
			const { productReqs } = this.campaigns[campaignIdx].onboard_settings;

			let count = 0;
			const targetIdx = productReqs
					.findIndex(productReq => productReq.product_template === product_template);

			if(targetIdx > -1) {
				// Update the requirement
				productReqs.splice(targetIdx, 1, {
					...productReqs[targetIdx],
					...this.newOrEditProductReq
				});

				// Clear it
				this.newOrEdit = null;
				this.newOrEditProductReq = null;
				this.newOrEditCampaignIdx = null;
			}
			else {
				console.error("Index out of bounds or item not found");
			}

			// set the form state to dirty
			this.setDirtyState(true);
		}
	},
	watch: {
		campaigns: {
			handler: 'updateLimitChildTemplates',
			deep: true,
		},
		mainCampaignFormIsValid: {
			deep: true,
			// immediate: true,
			handler(newValue, oldValue) {
				if(this.bypassDefaultRequired) {
					this.formIsValid.campaigns[0] = newValue;
				}
				else {
					if(!!newValue && !this.bypassDefaultRequired && !this.hasRequiredProductReqs())
						this.formIsValid.campaigns[0] = false;
				}
			}
		}
	}
}
</script>

<style lang="scss" scoped>
::v-deep #product-req-table {
	& td {
		vertical-align: top !important;
	}
}

tr.expanded {
	& td {
		background: #fafafa !important;

		&:last-child {
			width: 100%;
		}
	}
}

</style>